<template>
  <div class="kh-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$U_top ≠ U_bottom$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <!-- Particle/Wave Field -->
        <svg width="100%" height="240" viewBox="0 0 400 240">
           <defs>
              <linearGradient id="topSky" x1="0" y1="0" x2="0" y2="1">
                 <stop offset="0%" stop-color="rgba(56, 189, 248, 0.2)" />
                 <stop offset="100%" stop-color="rgba(56, 189, 248, 0.05)" />
              </linearGradient>
           </defs>
           
           <rect width="400" height="120" fill="url(#topSky)" />
           <rect y="120" width="400" height="120" fill="rgba(15, 23, 42, 0.5)" />

           <!-- Boundary Layer Waves -->
           <path :d="interfacePath" fill="none" stroke="#38bdf8" stroke-width="2.5" filter="url(#kh-glow)" />

           <!-- Vorticity Rollups -->
           <g v-for="(roll, i) in rolls" :key="i" :transform="`translate(${roll.x}, 120)`">
              <path :d="generateRoll(roll.amp)" 
                    fill="rgba(56, 189, 248, 0.15)" stroke="rgba(255,255,255,0.4)" 
                    stroke-width="1" stroke-dasharray="2,2" />
           </g>

           <!-- Speed Indicators -->
           <g transform="translate(20, 40)">
              <text fill="#cbd5e1" font-size="10" font-weight="bold">上层高速 U1</text>
              <line x1="0" y1="8" :x2="20 + velocity*3" y2="8" stroke="#38bdf8" stroke-width="2" marker-end="url(#kh-arr)" />
           </g>
           <g transform="translate(20, 200)">
              <text fill="#64748b" font-size="10" font-weight="bold">下层低速 U2</text>
              <line x1="0" y1="8" x2="20" y2="8" stroke="#64748b" stroke-width="2" marker-end="url(#kh-arr-dim)" />
           </g>

           <defs>
              <marker id="kh-arr" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                 <path d="M0,0 L6,2 L0,4 Z" fill="#38bdf8" />
              </marker>
              <marker id="kh-arr-dim" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                 <path d="M0,0 L6,2 L0,4 Z" fill="#64748b" />
              </marker>
              <filter id="kh-glow"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
           </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>剪切强度 ΔU</label>
              <span class="val">{{ (velocity - 1).toFixed(1) }} <small>units</small></span>
           </div>
           <div class="hud-item orange">
              <label>界面复杂度</label>
              <span class="val">{{ velocity > 6 ? '湍流卷吸' : '层流波动' }}</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item" style="grid-column: span 2;">
                 <label>调节层间速度差 (观察卷吸结构的形成)</label>
                 <el-slider v-model="velocity" :min="1" :max="12" :step="0.5" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>深度物理原理：</strong>当两层速度不同的流体接触时，交界面上会产生极大的速度梯度（剪切）。一个小微小的扰动会导致交界面发生形变，而流速差产生的伯努利效应会进一步放大这种形变，最终形成像**卷轴**或**巨浪**一样的螺旋结构。你在木星的大红斑边缘、天空中的“鱼鳞云”甚至海洋的高速暗流中经常能见到这种壮观的不稳定性现象。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KelvinHelmholtzViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      velocity: 5,
      time: 0,
      rolls: [
         { x: 50, amp: 1 }, { x: 150, amp: 1.2 }, { x: 250, amp: 0.9 }, { x: 350, amp: 1.1 }
      ],
      timer: null
    }
  },
  computed: {
    interfacePath() {
       let d = "M 0,120 ";
       const k = 0.05; // wave number
       for(let x=0; x<=400; x+=5) {
          // Combination of base sine and non-linear rollup
          let wave = Math.sin(x * k - this.time * this.velocity * 0.1) * (this.velocity * 2);
          // Nonlinear sharpening
          let rollup = Math.sin((x * k - this.time * 0.2) * 2) * (this.velocity * 0.5);
          d += `L ${x} ${120 + wave + rollup} `;
       }
       return d;
    }
  },
  methods: {
    generateRoll(baseAmp) {
       // A spiral-like path representing the vortex core
       const scale = this.velocity * baseAmp;
       return `M -20,0 Q -10,${-scale*2} 20,${-scale} T 0,${scale/2} T -15,0`;
    },
    animate() {
       this.time += 1;
       this.rolls.forEach(r => {
          r.x = (r.x + this.velocity * 0.2) % 450;
          if (r.x > 400) r.x = -50;
       });
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.kh-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdfa; color: #0d9488; border: 1px solid #ccfbf1; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: flex-start; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
